import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import ScrollToTop from '../components/ScrollToTop';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
          <p className="text-gray-600 mb-6">Effective Date: 2025-12-07</p>
          
          <div className="prose max-w-none">
            <p className="mb-4">
              This Privacy Policy applies to the services provided by the Service Provider through the official website and Artificial Intelligence (AI)-based Customer Service via WhatsApp (hereinafter referred to as the "Service").
            </p>
            
            <p className="mb-4">
              This Service is provided on an "AS IS" basis.
            </p>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Information Collection and Use</h2>
            <p className="mb-4">
              The Service may collect information when you:
            </p>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2">Visit the website</li>
              <li className="mb-2">Communicate with the AI Customer Service on WhatsApp</li>
              <li className="mb-2">Use features or submit forms on the website</li>
            </ul>
            
            <p className="mb-4">
              The types of information collected may include:
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Automatically Collected Information</h3>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2">IP address</li>
              <li className="mb-2">Browser type and version</li>
              <li className="mb-2">Pages visited and time spent</li>
              <li className="mb-2">Access date and time</li>
              <li className="mb-2">Device type and operating system</li>
              <li className="mb-2">Referring website addresses</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">User-Provided Information</h3>
            <p className="mb-4">
              When you interact with our WhatsApp AI or website forms, you may provide:
            </p>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2">Name</li>
              <li className="mb-2">Phone number</li>
              <li className="mb-2">Email address</li>
              <li className="mb-2">Message content</li>
              <li className="mb-2">Any other data you voluntarily submit</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Location Information</h3>
            <p className="mb-4">
              The Service does not collect precise GPS data.
            </p>
            <p className="mb-4">
              However, approximate location data may be derived from IP address for:
            </p>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2">Security and fraud prevention</li>
              <li className="mb-2">Website analytics</li>
              <li className="mb-2">Service improvement</li>
            </ul>
            <p className="mb-4">
              No real-time tracking is performed.
            </p>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Use of Information</h2>
            <p className="mb-4">
              Collected data is used to:
            </p>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2">Provide customer support</li>
              <li className="mb-2">Deliver service features</li>
              <li className="mb-2">Improve system performance</li>
              <li className="mb-2">Conduct analysis and research</li>
              <li className="mb-2">Provide important updates or service notifications</li>
              <li className="mb-2">Prevent misuse and fraud</li>
              <li className="mb-2">Send promotional or marketing information (when permitted by law)</li>
            </ul>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Use of Artificial Intelligence</h2>
            <p className="mb-4">
              The Service uses Artificial Intelligence (AI) for:
            </p>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2">Automated customer responses on WhatsApp</li>
              <li className="mb-2">Query classification and routing</li>
              <li className="mb-2">Basic personalization</li>
            </ul>
            <p className="mb-4">
              AI systems may process conversation data to improve service quality.
            </p>
            <p className="mb-4">
              AI does not make legal, medical, or financial decisions.
            </p>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Third-Party Sharing</h2>
            <p className="mb-4">
              The Service Provider may share data with:
            </p>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2">Hosting and infrastructure providers</li>
              <li className="mb-2">Analytics services</li>
              <li className="mb-2">WhatsApp service providers</li>
              <li className="mb-2">Cloud service operators</li>
            </ul>
            <p className="mb-4">
              Data sharing is limited to what is necessary to operate the Service.
            </p>
            <p className="mb-4">
              Information may be disclosed:
            </p>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2">When legally required</li>
              <li className="mb-2">To prevent fraud or harm</li>
              <li className="mb-2">To comply with government requests or court orders</li>
            </ul>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Data Retention Policy</h2>
            <p className="mb-4">
              User data is stored:
            </p>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2">As long as necessary to provide service</li>
              <li className="mb-2">For audit, security, and legal compliance</li>
              <li className="mb-2">Then securely deleted or anonymized</li>
            </ul>
            <p className="mb-4">
              You may request deletion of your data at: 📧 hello@sabisolution.com
            </p>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Children's Privacy</h2>
            <p className="mb-4">
              The Service is not intended for children under the age of 13.
            </p>
            <p className="mb-4">
              The Service Provider does not knowingly collect information from minors.
            </p>
            <p className="mb-4">
              If data from a child is discovered, it will be removed promptly.
            </p>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Security Measures</h2>
            <p className="mb-4">
              The Service Provider applies:
            </p>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2">Technical safeguards</li>
              <li className="mb-2">Access controls</li>
              <li className="mb-2">Encryption where appropriate</li>
              <li className="mb-2">Secure hosting environments</li>
            </ul>
            <p className="mb-4">
              However, no system is 100% secure.
            </p>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">International Data Transfers</h2>
            <p className="mb-4">
              Data may be processed in locations outside your country depending on hosting providers.
            </p>
            <p className="mb-4">
              All transfers are protected according to applicable data protection laws.
            </p>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Your Rights</h2>
            <p className="mb-4">
              Depending on local law, you may have the right to:
            </p>
            <ul className="list-disc pl-6 mb-6">
              <li className="mb-2">Access your data</li>
              <li className="mb-2">Correct inaccuracies</li>
              <li className="mb-2">Request deletion</li>
              <li className="mb-2">Object to processing</li>
              <li className="mb-2">Withdraw consent</li>
            </ul>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Changes to This Privacy Policy</h2>
            <p className="mb-4">
              The Service Provider may update this Privacy Policy from time to time.
            </p>
            <p className="mb-4">
              All changes will be published on the website.
            </p>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Your Consent</h2>
            <p className="mb-4">
              By accessing our website or contacting the AI service on WhatsApp, you agree to this Privacy Policy.
            </p>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Contact Us</h2>
            <p className="mb-4">
              For privacy-related matters, contact:
            </p>
            <div className="bg-gray-100 p-4 rounded-md mb-6">
              <p>📧 hello@sabisolution.com</p>
            </div>
          </div>
          
          <div className="mt-8">
            <Link to="/" className="text-primary-600 hover:text-primary-800">
              &larr; Back to Home
            </Link>
          </div>
        </div>
      </div>
      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default PrivacyPolicyPage;